import { Menu, X } from "lucide-react";
import { useState } from "react";

const NAV_LINKS = [
	{ label: "Features", href: "#features" },
	{ label: "Preview", href: "#preview" },
	{ label: "Pricing", href: "#pricing" },
	{ label: "FAQ", href: "#faq" },
	{ label: "Blog", href: "/blog/" },
	{ label: "About", href: "/about/" },
	{ label: "Download", href: "#download" },
];

const BLOG_NAV_LINKS = [
	{ label: "Home", href: "/" },
	{ label: "Blog", href: "/blog/" },
	{ label: "About", href: "/about/" },
	{ label: "Pricing", href: "/#pricing" },
	{ label: "Download", href: "/#download" },
];

interface NavbarProps {
	pathname?: string;
}

export function Navbar({ pathname = "/" }: NavbarProps) {
	const [mobileOpen, setMobileOpen] = useState(false);
	const isBlogPage = pathname.startsWith("/blog") || pathname.startsWith("/about");
	const navLinks = isBlogPage ? BLOG_NAV_LINKS : NAV_LINKS;
	const downloadHref = isBlogPage ? "/#download" : "#download";
	const pricingHref = isBlogPage ? "/#pricing" : "#pricing";

	return (
		<nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#0a0a0b]/75 backdrop-blur-2xl supports-[backdrop-filter]:bg-[#0a0a0b]/65">
			<div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
				<a
					href="/"
					className="flex items-center gap-2.5 group"
					aria-label="Go to the Clapio home page"
				>
					<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500 shadow-lg shadow-brand-500/20 transition-transform group-hover:scale-105">
						<svg viewBox="0 0 32 32" className="h-5 w-5">
							<circle cx="16" cy="16" r="6" fill="none" stroke="white" strokeWidth="2.5" />
							<circle cx="16" cy="16" r="2" fill="white" />
						</svg>
					</div>
					<div>
						<span className="block text-lg font-bold tracking-tight">Clapio</span>
						<span className="hidden text-[11px] uppercase tracking-[0.22em] text-white/35 sm:block">
							Screen recorder and editor
						</span>
					</div>
				</a>

				<div className="hidden items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.03] px-3 py-2 md:flex">
					{navLinks.map((link) => (
						<a
							key={link.href}
							href={link.href}
							className="rounded-full px-3 py-1.5 text-sm text-white/60 transition-colors hover:text-white"
						>
							{link.label}
						</a>
					))}
				</div>

				<div className="hidden items-center gap-3 md:flex">
					<a
						href={downloadHref}
						className="text-sm font-medium text-white/60 transition-colors hover:text-white"
					>
						Download
					</a>
					<a
						href={pricingHref}
						className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium transition-colors hover:bg-brand-400"
					>
						Get Started
					</a>
				</div>

				<button
					onClick={() => setMobileOpen(!mobileOpen)}
					className="rounded-lg p-2 text-white/60 transition-colors hover:text-white md:hidden"
					aria-expanded={mobileOpen}
					aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
				>
					{mobileOpen ? <X size={20} /> : <Menu size={20} />}
				</button>
			</div>

			{mobileOpen && (
				<div className="border-t border-white/[0.06] bg-[#0a0a0b]/95 px-6 py-4 backdrop-blur-xl md:hidden">
					<div className="space-y-2">
						{navLinks.map((link) => (
							<a
								key={link.href}
								href={link.href}
								onClick={() => setMobileOpen(false)}
								className="block rounded-lg px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/[0.04] hover:text-white"
							>
								{link.label}
							</a>
						))}
					</div>
					<a
						href={pricingHref}
						onClick={() => setMobileOpen(false)}
						className="mt-4 block rounded-xl bg-brand-500 px-4 py-2.5 text-center text-sm font-medium transition-colors hover:bg-brand-400"
					>
						Get Started
					</a>
				</div>
			)}
		</nav>
	);
}
